import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  Clapperboard,
  Home,
  ListChecks,
  LogOut,
  Megaphone,
  MessageSquareText,
  Plug,
  Send,
  Server,
  User,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';

import { authApi } from '../features/auth/api/auth.api';
import { useCurrentUser } from '../features/auth/hooks/useCurrentUser';
import { NotificacionesBell } from '../features/notificaciones/components/NotificacionesBell';
import { resolveAvatarSrc } from '../features/perfil/api/perfil.api';
import { useI18n } from '../i18n/I18nProvider';
import { cn } from '../lib/cn';

import { MarcaLogo } from './branding';
import { LanguageToggle } from './i18n/LanguageToggle';
import { ThemeToggle } from './theme/ThemeToggle';
import { Avatar } from './ui/avatar';
import { DropdownMenu, DropdownMenuItem, DropdownMenuSeparator } from './ui/dropdown-menu';

// Layout para rutas autenticadas: header sticky con logo + nav + user menu,
// contenido principal, footer institucional.
export function AppLayout({ children }: { children: ReactNode }): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: user } = useCurrentUser();
  const es = useI18n();
  const isAdmin = user?.rol === 'ADMINISTRADOR';

  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSettled: () => {
      // Limpiar TODO el cache de React Query — no solo qk.auth.me. Si solo
      // limpiamos el current user, el resto de queries sigue mostrando data del
      // usuario anterior durante el staleTime (30s), filtrada con sus permisos.
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-black/20 bg-marca-primario-oscuro px-6 py-2.5 shadow-md">
        <div className="flex items-center gap-8">
          <Link to="/" className="shrink-0" aria-label="Inicio">
            <MarcaLogo variant="white" className="h-7" />
          </Link>
          <nav className="flex items-center gap-1">
            <NavItem to="/" label={es.nav.inicio} icon={<Home size={15} />} />
            <NavItem to="/bandeja" label={es.nav.bandeja} icon={<MessageSquareText size={15} />} />
            <NavItem to="/publicar" label={es.nav.publicar} icon={<Clapperboard size={15} />} />
            <NavItem to="/tareas" label={es.nav.tareas} icon={<ListChecks size={15} />} />
            <NavItem to="/campanas" label={es.nav.campanas} icon={<Send size={15} />} />
            <NavItem to="/conexiones" label={es.nav.conexiones} icon={<Plug size={15} />} />
            {isAdmin && (
              <>
                <NavItem to="/admin/campanas" label={es.nav.anuncios} icon={<Megaphone size={15} />} />
                <NavItem to="/admin/sistema" label={es.nav.sistema} icon={<Server size={15} />} />
                {/* Usuarios, Errores, Configuración, API keys y Métricas
                    quedan fuera de la barra principal; se acceden por URL
                    directa. Las rutas siguen protegidas por RBAC (solo
                    ADMINISTRADOR). */}
              </>
            )}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <LanguageToggle tone="onDark" />
          <ThemeToggle tone="onDark" />
          {user && <NotificacionesBell tone="light" />}
          {user && (
            <DropdownMenu
              align="right"
              triggerAriaLabel={es.nav.menuUsuario}
              trigger={
                <span className="flex items-center gap-2 rounded-md border-l border-white/25 py-1 pl-3 pr-2 text-left leading-tight transition-colors hover:bg-white/10">
                  <Avatar
                    src={resolveAvatarSrc(user)}
                    nombre={user.nombre}
                    size={32}
                    className="bg-white/15 text-white ring-1 ring-white/40"
                  />
                  <span className="hidden sm:block">
                    <span className="block text-sm font-medium text-white">{user.nombre}</span>
                    <span className="block text-xs text-white/70">{user.rol}</span>
                  </span>
                  <ChevronDown size={14} className="text-white/70" />
                </span>
              }
            >
              <DropdownMenuItem icon={<User size={14} />} onClick={() => navigate('/perfil')}>
                {es.nav.miPerfil}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="danger"
                icon={<LogOut size={14} />}
                disabled={logoutMutation.isPending}
                onClick={() => logoutMutation.mutate()}
              >
                {es.auth.logout}
              </DropdownMenuItem>
            </DropdownMenu>
          )}
        </div>
      </header>

      <main className="flex-1 bg-surface-sunken">{children}</main>

      <footer className="flex items-center justify-between gap-4 border-t border-border-subtle bg-surface px-6 py-3 text-xs text-text-faint">
        <span>
          © {new Date().getFullYear()} {es.app.organizacion} · {es.app.equipo}
        </span>
        <MarcaLogo variant="primary" className="h-5 opacity-60" />
      </footer>
    </div>
  );
}

function NavItem({ to, label, icon }: { to: string; label: string; icon?: ReactNode }): JSX.Element {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        cn(
          'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
          isActive
            ? 'bg-white/20 font-medium text-white'
            : 'text-white/75 hover:bg-white/10 hover:text-white',
        )
      }
    >
      {/* Con 8 ítems (ciclo-emisor sumó Campañas), a 1280 px la barra se encimaba
          101 px con el idioma y el tema (medido en el DOM). Los íconos se muestran
          recién desde 1440 px: debajo, solo el texto. */}
      {icon && <span className="hidden min-[1440px]:inline-flex">{icon}</span>}
      {label}
    </NavLink>
  );
}
